import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { noop } from 'lodash-es';
import { POLL_INTERVAL_MS, POLL_MAX_ATTEMPTS } from '~/components/ai/SingleDraftAgentPane/SingleDraftAgentPane.utils';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { CONTINUE_POLLING, DONE_POLLING, pollUntil } from '~/utils/pollUntil/pollUntil';
import { sleepWithAbort } from '~/utils/sleepWithAbort';
import { isRenderablePart, isRetrievableConversation, reconcileServerConversation, readConversationIdFromLocationState, withConversationId, withoutConversationId } from '../AnalystAgentPane.utils';
import { useAnalystChatStore } from '../store/analystChatStore';
const NOT_RETRIEVABLE_STATUSES = new Set([
    403,
    404,
    410
]);
const isNotRetrievableError = (err)=>typeof (err === null || err === void 0 ? void 0 : err.status) === 'number' && NOT_RETRIEVABLE_STATUSES.has(err.status);
export const useAnalystConversation = function useAnalystConversation() {
    let options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
    var _options_pollIntervalMs;
    const pollIntervalMs = (_options_pollIntervalMs = options.pollIntervalMs) !== null && _options_pollIntervalMs !== void 0 ? _options_pollIntervalMs : POLL_INTERVAL_MS;
    const { api } = useStoreActions();
    const { t } = useLocaleKeys('ai.analyst');
    const location = useLocation();
    const navigate = useNavigate();
    const abortRef = useRef(null);
    const turnInFlightRef = useRef(false);
    const createInFlightRef = useRef(null);
    const initialConversationIdRef = useRef(readConversationIdFromLocationState(location.state));
    const hasRestoredRef = useRef(false);
    useEffect(()=>()=>{
            var _abortRef_current;
            return (_abortRef_current = abortRef.current) === null || _abortRef_current === void 0 ? void 0 : _abortRef_current.abort();
        }, []);
    const writeConversationReference = (id)=>{
        navigate({
            pathname: location.pathname,
            search: location.search,
            hash: location.hash
        }, {
            replace: true,
            state: withConversationId(location.state, id)
        });
    };
    const clearConversationReference = ()=>{
        navigate({
            pathname: location.pathname,
            search: location.search,
            hash: location.hash
        }, {
            replace: true,
            state: withoutConversationId(location.state)
        });
    };
    const phaseLabel = (phase)=>{
        if (phase === 'understanding') return t.thinking.understanding();
        if (phase === 'querying') return t.thinking.querying();
        return t.thinking.summarizing();
    };
    const handleEvent = (event)=>{
        const store = useAnalystChatStore.getState();
        switch(event.type){
            case 'turn_start':
                store.beginTurn();
                return;
            case 'status':
                {
                    const label = typeof event.label === 'string' ? event.label.trim() : '';
                    store.setThinkingLabel(label.length > 0 ? label : phaseLabel(event.phase));
                    return;
                }
            case 'content_part':
                if (isRenderablePart(event.part)) store.appendAssistantContent(event.part);
                return;
            case 'turn_done':
                store.markTurnDone(event.messageId);
                return;
            case 'stream_error':
                store.failTransport(event.code);
                return;
        }
    };
    const handleNotRetrievable = ()=>{
        useAnalystChatStore.getState().reset();
        useAnalystChatStore.getState().failRecovery(false);
        clearConversationReference();
    };
    const pollUntilIdle = async (id, signal)=>{
        const outcome = await pollUntil({
            initialState: undefined,
            attempt: async (_state, attemptIndex)=>{
                try {
                    const conversation = await api.analyticAgent.get(id);
                    if (signal.aborted) return CONTINUE_POLLING;
                    if (!isRetrievableConversation(conversation)) {
                        handleNotRetrievable();
                        return DONE_POLLING;
                    }
                    if (conversation.turn.state !== 'idle') return CONTINUE_POLLING;
                    useAnalystChatStore.getState().hydrate(reconcileServerConversation(conversation));
                    writeConversationReference(id);
                    return DONE_POLLING;
                } catch (err) {
                    if (isNotRetrievableError(err)) {
                        handleNotRetrievable();
                        return DONE_POLLING;
                    }
                    if (attemptIndex < POLL_MAX_ATTEMPTS - 1) return CONTINUE_POLLING;
                    useAnalystChatStore.getState().failRecovery(true);
                    return DONE_POLLING;
                }
            },
            maxAttempts: POLL_MAX_ATTEMPTS,
            delayMs: pollIntervalMs,
            waitAfterLastAttempt: true,
            wait: (ms)=>sleepWithAbort(ms, signal).catch(noop),
            shouldStop: ()=>signal.aborted
        });
        if (outcome.kind === 'exhausted') useAnalystChatStore.getState().failRecovery(true);
    };
    const recover = async (id, signal)=>{
        useAnalystChatStore.getState().startConversation(id);
        useAnalystChatStore.getState().beginRecovery();
        await pollUntilIdle(id, signal);
    };
    useEffect(()=>{
        if (hasRestoredRef.current) return;
        hasRestoredRef.current = true;
        const store = useAnalystChatStore.getState();
        if (store.conversationId != null || store.messages.length > 0) return;
        const id = initialConversationIdRef.current;
        if (id == null) return;
        const controller = new AbortController();
        abortRef.current = controller;
        void recover(id, controller.signal);
    // WHY: mount-only restore — re-running on recover/location identity changes would re-fetch the thread.
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
    }, []);
    const ensureConversationId = (signal)=>{
        const existing = useAnalystChatStore.getState().conversationId;
        if (existing != null) return Promise.resolve(existing);
        if (createInFlightRef.current != null) return createInFlightRef.current;
        const pending = api.analyticAgent.create().then((param)=>{
            let { conversationId } = param;
            if (!signal.aborted) {
                useAnalystChatStore.getState().startConversation(conversationId);
                writeConversationReference(conversationId);
            }
            return conversationId;
        }).finally(()=>{
            createInFlightRef.current = null;
        });
        createInFlightRef.current = pending;
        return pending;
    };
    const runTurn = async (text, appendUser)=>{
        if (turnInFlightRef.current) return;
        turnInFlightRef.current = true;
        const store = useAnalystChatStore.getState();
        if (appendUser) store.appendUserMessage(text);
        store.beginTurn();
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const conversationId = await ensureConversationId(controller.signal);
            if (controller.signal.aborted) return;
            await api.analyticAgent.sendMessage({
                conversationId,
                content: text,
                signal: controller.signal,
                onEvent: handleEvent
            });
            if (!controller.signal.aborted) useAnalystChatStore.getState().finalizeTurn();
        } catch (unused) {
            if (controller.signal.aborted) {
                useAnalystChatStore.getState().finalizeTurn();
                return;
            }
            const id = useAnalystChatStore.getState().conversationId;
            if (id != null) await recover(id, controller.signal);
            else useAnalystChatStore.getState().failTransport('unreachable');
        } finally{
            turnInFlightRef.current = false;
        }
    };
    const send = (text)=>{
        const trimmed = text.trim();
        if (trimmed.length === 0) return;
        void runTurn(trimmed, true);
    };
    const retry = ()=>{
        const state = useAnalystChatStore.getState();
        if (state.recoveryFailed && state.recoveryRetryable && state.conversationId != null) {
            const controller = new AbortController();
            abortRef.current = controller;
            void recover(state.conversationId, controller.signal);
            return;
        }
        const lastUserMessage = [
            ...state.messages
        ].reverse().find((message)=>message.role === 'user');
        if ((lastUserMessage === null || lastUserMessage === void 0 ? void 0 : lastUserMessage.role) === 'user') void runTurn(lastUserMessage.text, false);
    };
    const reset = ()=>{
        var _abortRef_current;
        (_abortRef_current = abortRef.current) === null || _abortRef_current === void 0 ? void 0 : _abortRef_current.abort();
        turnInFlightRef.current = false;
        createInFlightRef.current = null;
        useAnalystChatStore.getState().reset();
        clearConversationReference();
    };
    const submitFeedback = async (messageId, rating, comment)=>{
        const state = useAnalystChatStore.getState();
        if (state.conversationId == null) throw new Error('Cannot submit analyst feedback without an active conversation');
        if (state.feedbackEligibleMessageId !== messageId) return;
        await api.analyticAgent.feedback({
            conversationId: state.conversationId,
            messageId,
            rating,
            comment
        });
    };
    const markAnswerRated = (messageId)=>{
        useAnalystChatStore.getState().markFeedbackSubmitted(messageId);
    };
    return {
        send,
        retry,
        reset,
        submitFeedback,
        markAnswerRated
    };
};
